import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/products';
export const supplierProductsEndpointIdentifier = endpoint;

export interface SupplierProductDto {
  id: number;
  name: {
    en: string;
    ar: string;
  };
  brand?: {
    en: string;
    ar: string;
  };
  code?: string;
  price?: number;
  defaultPrice?: number;
  defaultUnit?: string;
  // Some response shapes carry the unit nested instead of flat.
  unit?: { id: number; abbreviation: string } | null;
  specs?: string | null;
  origin?: string | null;
  state?: string | null;
  catalogCategoryId?: number | null;
  categoryTitle?: string | null;
  category?: { id: number; title?: string } | null;
  imageUrl?: string | null;
  description?: string;
  currency?: string;
  isActive?: boolean;
}

interface ResponseData {
  data: SupplierProductDto[];
  total: number;
  page: number;
  limit: number;
  pageCount: number;
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, { supplierId: number }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'> & {
  enabled?: boolean;
};
type ReturnType = UseQueryResult<TData, TError>;

/**
 * Fetch products for a specific supplier.
 * Uses /ums/v1/products with supplierId and supplierType=supplier
 */
export function useGetSupplierProductsApi(supplierId: number | null, _search?: string, options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();
  const { enabled: userEnabled = true, ...restOptions } = options || {};

  const queryKey: TQueryKey = [endpoint, { supplierId: supplierId || 0 }];

  const queryFn = async (): Promise<TData> => {
    try {
      const params: Record<string, any> = {
        supplierId,
        supplierType: 'supplier',
        limit: 100,
        page: 1,
      };

      const { data } = await http.get(endpoint, { params });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(queryKey, queryFn, {
    enabled: isLoggedIn && userEnabled && !!supplierId,
    staleTime: 5 * 60 * 1000,
    ...restOptions,
  });
}
